// API Service for Django Backend
import logger from './logger';

// User-facing message when rate limit is reached (non-technical, professional)
export const RATE_LIMIT_MESSAGE =
  "You're making requests a bit too quickly. Please wait a moment and try again.";

// Shown when the server cannot be reached (network / connection error)
export const NETWORK_ERROR_MESSAGE =
  "Couldn't connect to the server. Please check that the backend is running and try again.";

/** Get Django CSRF token from cookie for admin API requests (session auth). */
function getCsrfToken() {
  if (typeof document === 'undefined' || !document.cookie) return null;
  const m = document.cookie.match(/\bcsrftoken=([^;]+)/);
  return m ? decodeURIComponent(m[1]) : null;
}

/** True if this endpoint requires Django admin session (credentials + CSRF). */
function isAdminEndpoint(endpoint) {
  return endpoint.startsWith('/admin/');
}

class ApiService {
  constructor() {
    // Use relative /api so same origin in dev (with proxy) and production. Override with REACT_APP_API_URL if needed.
    this.baseURL = process.env.REACT_APP_API_URL || '/api';
    this.sessionId = this.getOrCreateSessionId();
  }

  // Get existing session ID or create a new one
  getOrCreateSessionId() {
    const STORAGE_KEY = 'radio_session_id';
    const SESSION_DURATION = 24 * 60 * 60 * 1000; // 24 hours in milliseconds
    
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const sessionData = JSON.parse(stored);
        const now = Date.now();
        
        // Check if session is still valid (within 24 hours)
        if (now - sessionData.createdAt < SESSION_DURATION) {
          // Update last activity time
          sessionData.lastActivity = now;
          localStorage.setItem(STORAGE_KEY, JSON.stringify(sessionData));
          return sessionData.sessionId;
        } else {
          // Session expired, create new one
          localStorage.removeItem(STORAGE_KEY);
        }
      }
    } catch (error) {
      // If localStorage fails, continue with new session
      console.debug('Failed to read session from localStorage:', error);
    }
    
    // Create new session
    const newSessionId = this.generateSessionId();
    const sessionData = {
      sessionId: newSessionId,
      createdAt: Date.now(),
      lastActivity: Date.now()
    };
    
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(sessionData));
    } catch (error) {
      console.debug('Failed to save session to localStorage:', error);
    }
    
    return newSessionId;
  }

  // Generate unique session ID
  generateSessionId() {
    return Date.now().toString(36) + Math.random().toString(36).substr(2);
  }

  // Generic API request method
  async request(endpoint, options = {}) {
    const url = `${this.baseURL}${endpoint}`;
    const isAdmin = isAdminEndpoint(endpoint);
    const headers = {
      'Content-Type': 'application/json',
      ...options.headers,
    };
    if (isAdmin) {
      const csrf = getCsrfToken();
      if (csrf) headers['X-CSRFToken'] = csrf;
    }
    const config = {
      headers,
      credentials: isAdmin ? 'include' : 'same-origin',
      ...options,
    };

    try {
      logger.debug(`Making API request to: ${url}`);
      const response = await fetch(url, config);

      if (response.status === 401 && isAdmin) {
        const loginPath = '/admin/login/';
        const next = encodeURIComponent(window.location.pathname + window.location.search);
        window.location.href = `${loginPath}?next=${next}`;
        throw new Error('Authentication required');
      }

      if (!response.ok) {
        let message = `HTTP error! status: ${response.status}`;
        if (response.status === 429) {
          try {
            await response.json(); // consume body
          } catch {
            /* ignore */
          }
          message = RATE_LIMIT_MESSAGE;
        }
        logger.error(`API request failed for ${endpoint}: ${message}`, {
          url,
          status: response.status,
          statusText: response.statusText,
          config
        });
        throw new Error(message);
      }

      const data = await response.json();
      logger.debug(`API response for ${endpoint}:`, data);
      return data;
    } catch (error) {
      logger.error(`API request failed for ${endpoint}:`, {
        url,
        error: error.message,
        config
      });
      // Show a clear message for network/connection failures instead of "Failed to fetch"
      const isNetworkError =
        !error.response &&
        (error.message === 'Failed to fetch' ||
          error.message === 'Load failed' ||
          error.name === 'TypeError');
      if (isNetworkError) {
        throw new Error(NETWORK_ERROR_MESSAGE);
      }
      throw error;
    }
  }

  // ===== COUNTRIES API =====
  async getCountries(search = '', page = 1, pageSize = 50) {
    const params = new URLSearchParams({
      search,
      page: page.toString(),
      page_size: pageSize.toString(),
    });
    return this.request(`/countries/?${params}`);
  }

  async getCountryDetail(countryId) {
    return this.request(`/countries/${countryId}/`);
  }

  async searchCountries(query) {
    return this.request(`/countries/?search=${encodeURIComponent(query)}&page_size=1000`);
  }

  async trackCountryPlay(countryId) {
    return this.request(`/countries/${countryId}/play/`, {
      method: 'POST',
    });
  }

  // ===== STATIONS API =====
  async getStations(country = '', search = '', page = 1, pageSize = 100) {
    const params = new URLSearchParams({
      search,
      page: page.toString(),
      page_size: pageSize.toString(),
    });
    
    // If country is provided, add it as country__name parameter
    if (country) {
      params.append('country__name', country);
    }
    
    return this.request(`/stations/?${params}`);
  }

  async getStationDetail(stationId) {
    return this.request(`/stations/${stationId}/`);
  }

  async searchStations(query) {
    return this.request(`/search/stations/?q=${encodeURIComponent(query)}`);
  }

  // Public sync status for main app (no auth)
  async getSyncStatus() {
    return this.request('/sync/status/');
  }

  async trackStationPlay(stationId) {
    return this.request(`/stations/${stationId}/play/`, {
      method: 'POST',
    });
  }

  async trackStationError(stationId) {
    return this.request(`/stations/${stationId}/error/`, {
      method: 'POST',
    });
  }

  async trackStationPlaySession(sessionData) {
    return this.request('/analytics/station-play-session/', {
      method: 'POST',
      body: JSON.stringify(sessionData),
    });
  }

  // ===== CONTENT API =====
  async getAllContent() {
    return this.request('/content/all/');
  }

  async getContentByType(contentType) {
    return this.request(`/content/type/${contentType}/`);
  }

  // ===== ANALYTICS API =====
  async trackVisitor() {
    const visitorData = {
      session_id: this.sessionId,
      user_agent: navigator.userAgent,
      screen_resolution: `${window.screen.width}x${window.screen.height}`,
      language: navigator.language,
    };

    return this.request('/analytics/visitor/', {
      method: 'POST',
      body: JSON.stringify(visitorData),
    });
  }

  async submitContactForm(formData) {
    return this.request('/contact/', {
      method: 'POST',
      body: JSON.stringify(formData),
    });
  }

  async getAnalyticsStats() {
    return this.request('/analytics/stats/');
  }

  async getDailyStats(date) {
    return this.request(`/analytics/daily-stats/?date=${date}`);
  }

  async getLocationStats() {
    return this.request('/analytics/location-stats/');
  }

  // ===== ENHANCED ANALYTICS API =====
  async trackAudioEngagement(data) {
    return this.request('/analytics/audio-engagement/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async trackUserJourney(data) {
    return this.request('/analytics/user-journey/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async trackSearchAnalytics(data) {
    console.log('📡 Sending search analytics to API:', data);
    return this.request('/analytics/search-analytics/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async trackVisitorSession(data) {
    return this.request('/analytics/visitor-session/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getEngagementStats() {
    return this.request('/analytics/engagement-stats/');
  }

  async getVisitorNumbers() {
    return this.request('/analytics/visitor-numbers/');
  }

  // ===== SONG IDENTIFICATION =====
  async identifySong(streamUrl) {
    return this.request('/stations/identify/', {
      method: 'POST',
      body: JSON.stringify({ stream_url: streamUrl }),
    });
  }

  async getArtistLinks(artistName) {
    return this.request('/stations/artist-links/', {
      method: 'POST',
      body: JSON.stringify({ artist_name: artistName }),
    });
  }

  // ===== SYNC API =====
  // getSyncStatus method already defined above

  async triggerSync() {
    return this.request('/admin/sync/trigger/', {
      method: 'POST',
    });
  }

  async getSyncConfig() {
    const data = await this.request('/admin/sync/status/');
    return data?.config != null ? { success: true, config: data.config } : data;
  }

  async updateSyncConfig(config) {
    return this.request('/admin/sync/config/', {
      method: 'PUT',
      body: JSON.stringify(config),
    });
  }

  async getSyncLogs() {
    return this.request('/admin/sync/logs/');
  }

  // ===== ADMIN API =====
  async getAdminDashboard() {
    return this.request('/admin/dashboard/');
  }

  async toggleCountryStatus(countryId) {
    return this.request(`/admin/countries/${countryId}/toggle/`, {
      method: 'PUT',
    });
  }

  async toggleStationStatus(stationId) {
    return this.request(`/admin/stations/${stationId}/toggle/`, {
      method: 'PUT',
    });
  }

  async getAdminCountries() {
    return this.request('/admin/countries/');
  }

  async getAdminStations() {
    return this.request('/admin/stations/');
  }

  /** Base URL for API (e.g. /api). Used to build stream proxy URL on HTTPS. */
  getBaseURL() {
    return this.baseURL || '/api';
  }
}

// Create singleton instance
const apiService = new ApiService();
export default apiService;
